import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { useChat } from '../../../hooks/useChat';
import { useFetch } from '../../../hooks/useFetch';

const readFileBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result.split(',').pop());
  reader.onerror = reject;
});

const serializeFile = async (file) => ({
  lastModified: file.lastModified,
  lastModifiedDate: file.lastModifiedDate,
  name: file.name,
  size: file.size,
  type: file.type,
  data: await readFileBase64(file),
});

export const useFileUploader = () => {
  const { fetchInstance } = useFetch();
  const { publish, fetchHistory, chatId } = useChat();
  const [isPending, setIsPending] = useState(false);
  const [files, setFiles] = useState([]);

  const upload = useCallback(
    async (file) => {
      try {
        const response = await fetchInstance.post(
          `/chat/${chatId}/upload`,
          JSON.stringify(await serializeFile(file)),
          {
            'Content-Type': 'application/json',
          },
        );
        if (!response.ok) {
          publish({
            variant: 'danger',
            text: `Failed to upload file: ${response.statusText}`,
          });
        } else {
          fetchHistory();
        }
      } catch (e) {
        publish({
          variant: 'danger',
          text: `Failed to upload file: ${e}`,
        });
      }
    },
    [publish, chatId, fetchInstance, fetchHistory],
  );

  const onDrop = (acceptedFiles, fileRejections) => {
    if (fileRejections.length > 0) {
      publish({ variant: 'danger', text: 'File too big' });

      return;
    }

    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i];
      const isAlreadyInFiles = files.filter((f) => f.name === file.name && f.size === file.size)
        .length > 0;
      if (isAlreadyInFiles) {
        publish({
          variant: 'warning',
          text: `${file.name} was already added`,
        });
        acceptedFiles.splice(i, 1);
      }
    }
    setFiles((filesUp) => [...filesUp, ...acceptedFiles]);
  };

  const uploadAllFiles = async () => {
    if (files.length === 0) {
      publish({
        text: 'Please, add files to upload',
        variant: 'warning',
      });

      return;
    }
    setIsPending(true);
    await Promise.all(files.map(upload));
    setFiles([]);
    setIsPending(false);
  };

  const {
    getInputProps, getRootProps, isDragActive, open,
  } = useDropzone({
    onDrop,
    noClick: true,
    maxSize: 100000000, // 1 MB
  });

  return {
    isPending,
    getInputProps,
    getRootProps,
    isDragActive,
    open,
    uploadAllFiles,

    files,
    setFiles,
  };
};

export default useFileUploader;
